import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CaseModel, InitialFlipType, NextrayModel, StateTypes } from '@ortho-next/nextray-core';
import { combineLatest, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Bridge } from '../../nextray/Core/Bridge';
import { Main } from '../../nextray/Core/Main';
import { TemplateModel } from '../../nextray/Core/Save';
import { AppModel } from '../../nextray/Models/AppModel';
import { DeformityAppModel } from '../../nextray/Models/DeformityAppModel';
import { State } from '../../nextray/States/State';
import { APP_CONFIG, Image, ImageOrientationEnum, isGuid, LanguageService, Plan, RayModel } from '../core';
import { CaseService } from './case.service';
import { ImageService } from './image.service';
import { ModelService } from './model.service';


export interface Canvas3DReadyArgs {
	bridge: Bridge;
	model: AppModel;
	deformityModel: DeformityAppModel;
}


/**
* This service initialized the canvas component.
*/
@Injectable({ providedIn: 'root' })
export class MainCanvasLoaderService {

	private readonly titleSrv = inject(Title);
	private readonly config = inject(APP_CONFIG);
	private readonly caseSrv = inject(CaseService);
	private readonly imgSrv = inject(ImageService);
	private readonly route = inject(ActivatedRoute);
	private readonly modelSrv = inject(ModelService);
	private readonly langSrv = inject(LanguageService);

	private _main?: Main;
	private _initialized = false;
	private _canvas3DArgs?: Canvas3DReadyArgs;

	public canvas3D = new Subject<Canvas3DReadyArgs>();


	/**
	 * Prepare canvas component.
	 */
	public init(): Observable<void> {
		if (!this._initialized) {
			this._initialized = true;

			try {
				this._main = new Main(document.getElementById('canvas-container') as HTMLDivElement);
				this._canvas3DArgs = {
					bridge: this._main.bridge,
					model: this._main.model,
					deformityModel: this._main.deformityModel
				};
				this.canvas3D.next(this._canvas3DArgs);
				this.canvas3D.complete();
			} catch (error) {
				if (error instanceof Error && error.message === 'Error creating WebGL context.') {
					return throwError(() => new Error('WebglDisabled'));
				}
			}

			if (this.isScreenResolutionInvalid) {
				return throwError(() => new Error('ScreenResolutionInvalid'));
			}

			const caseGuid = this.route.snapshot.queryParamMap.get('caseGuid');
			return this.loadPlan(caseGuid!).pipe(
				switchMap(plan => this.loadModel(plan))
			);
		}
		return of();
	}

	private get isScreenResolutionInvalid(): boolean {
		// prevent mobile phone
		if (Math.min(window.innerWidth, window.innerHeight) < this.config.minScreenResolution.mobilePhoneWidth) return true;

		const browserZoom = window.outerWidth / window.innerWidth;
		const dprWithoutBrowserZoom = window.devicePixelRatio / browserZoom;

		const physicalWidth = screen.width * dprWithoutBrowserZoom;
		const physicalHeight = screen.height * dprWithoutBrowserZoom;

		return (physicalWidth < this.config.minScreenResolution.width - this.config.minScreenResolution.widthTolerance) || (physicalHeight < this.config.minScreenResolution.height - this.config.minScreenResolution.heightTolerance);
	}

	private loadPlan(planGuid: string): Observable<Plan> {
		if (!isGuid(planGuid)) return throwError(() => new Error('PlanNotValid'));
		return this.caseSrv.getCase(planGuid).pipe(
			catchError((err: Error) => {
				return err.message === 'ItemNotFound' ? throwError(() => new Error('PlanNotFound')) : throwError(() => err)
			}),
			switchMap(plan => {
				return plan.isReceived ? throwError(() => new Error('PlanNotValid')) : of(plan);
			}),
			tap(plan => this.setTitle(plan))
		);
	}

	private setTitle(plan: Plan): void {
		const titleLbl: string = this.langSrv.labels().APP_TITLE_PATIENT_PLAN;
		this.titleSrv.setTitle(titleLbl.replace('{PAT}', plan.patient.number).replace('{PLAN}', plan.number));
	}

	private loadModel(plan: Plan): Observable<void> {
		let nextrayModel: RayModel;
		return this.modelSrv.restoreRayModel(plan.id, plan.userGuid).pipe(
			switchMap(rayModel => {
				return this.isFitboneModel(rayModel) ? of(rayModel) : throwError(() => new Error('PlanNotValid'));
			}),
			tap(rayModel => nextrayModel = rayModel),
			switchMap(() => this.modelSrv.restoreTemplateModel(plan.id, plan.userGuid)),
			switchMap(fitboneModel => this.setMainModel(plan, fitboneModel?.model, nextrayModel?.model))
		);
	}

	private isFitboneModel(model: RayModel): boolean {
		return !!model?.model?.state && (JSON.parse(model?.model?.state) as unknown as State)?.type === StateTypes.RPM;
	}

	private readonly observAP = (plan: Plan) => plan.apImageGuid ? this.imgSrv.getImage(plan.apImageGuid) : of(null);
	private readonly observLT = (plan: Plan) => plan.ltImageGuid ? this.imgSrv.getImage(plan.ltImageGuid) : of(null);

	private setMainModel(plan: Plan, templateModel: TemplateModel, rayModel: NextrayModel): Observable<void> {
		return combineLatest([
			this.observAP(plan),
			this.observLT(plan)
		]).pipe(
			map(([imgAP, imgLT]) => this.caseModelMapper(plan, imgAP, imgLT, templateModel, rayModel)),
			switchMap(caseModel => from(this._main!.loadScenes(caseModel)))
		);
	}

	private caseModelMapper(plan: Plan, imgAP: Image | null, imgLT: Image | null, templateModel: TemplateModel, rayModel: NextrayModel): CaseModel {
		return {
			// plan data
			boneType: plan.boneType,
			version: plan.version,
			type: plan.type,
			referenceType: plan.referenceType,
			side: plan.side,
			isPostOperative: plan.isPostOperative,
			// image AP data
			imgAP: imgAP?.url,
			scaleFactorAP: imgAP?.scaleFactor,
			flipTypeAP: this.flipTypeMapper(imgAP?.orientation),
			brightnessAP: 0,
			contrastAP: 0,
			// image LT data
			imgLT: imgLT?.url,
			scaleFactorLT: imgLT?.scaleFactor,
			flipTypeLT: this.flipTypeMapper(imgLT?.orientation),
			brightnessLT: 0,
			contrastLT: 0,
			// ray model
			model: templateModel,
			cacheModel: rayModel
		}
	}

	private flipTypeMapper(orientationType: ImageOrientationEnum | undefined): InitialFlipType | undefined {
		switch (orientationType) {
			case ImageOrientationEnum.Nothing: return undefined;
			case ImageOrientationEnum.Horizontal: return InitialFlipType.horizontal;
			case ImageOrientationEnum.Vertical: return InitialFlipType.vertical;
			case ImageOrientationEnum.Rotate180: return InitialFlipType.rotate180;
			default: return undefined;
		}
	}

}
